
import React, { useState } from 'react';
import './NewPackageDialog.css';
import { isValidDate } from './financial';

const NewPackageDialog = ({ isOpen, onClose, onCreatePackage, onCheckPackageExists, setToast }) => {
  const [symbol, setSymbol] = useState('');
  const [packageTitle, setPackageTitle] = useState('');
  const [description, setDescription] = useState('');
  const [entityPrice, setEntityPrice] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [dividendYield, setDividendYield] = useState('0');
  const [tradingDate, setTradingDate] = useState(new Date().toISOString().split('T')[0]);
  const [confirmation, setConfirmation] = useState(null);

  if (!isOpen) {
    return null;
  }

  const handleCreate = async () => {
    const normalizedSymbol = symbol.trim().toUpperCase();
    const normalizedPackageTitle = packageTitle.trim().toUpperCase();
    const trimmedDescription = description.trim();
    if (normalizedSymbol !== symbol) {
      setSymbol(normalizedSymbol);
    }
    if (normalizedPackageTitle !== packageTitle) {
      setPackageTitle(normalizedPackageTitle);
    }
    if (trimmedDescription !== description) {
      setDescription(trimmedDescription);
    }

    if (!normalizedSymbol) {
      setToast({ message: 'Entity Symbol is required.', type: 'error' });
      return;
    }
    if (normalizedSymbol.length > 20) {
      setToast({ message: 'Entity Symbol must be 20 characters or fewer.', type: 'error' });
      return;
    }
    if (!/^[A-Z0-9.]+$/.test(normalizedSymbol)) {
      setToast({ message: 'Entity Symbol may contain only letters, numbers, and periods.', type: 'error' });
      return;
    }
    if (!normalizedPackageTitle) {
      setToast({ message: 'Package Title is required.', type: 'error' });
      return;
    }
    if (normalizedPackageTitle.length > 50) {
      setToast({ message: 'Package Title must be 50 characters or fewer.', type: 'error' });
      return;
    }
    if (!/^[A-Z0-9_.-]+$/.test(normalizedPackageTitle)) {
      setToast({ message: 'Package Title may contain only letters, numbers, underscores, hyphens, and periods.', type: 'error' });
      return;
    }
    if (trimmedDescription.length > 500) {
      setToast({ message: 'Description must be 500 characters or fewer.', type: 'error' });
      return;
    }
    if (!entityPrice || entityPrice <= 0) {
      setToast({ message: 'Entity Price is required and must be greater than 0.', type: 'error' });
      return;
    }
    if (interestRate === '' || interestRate < 0) {
      setToast({ message: 'Interest Rate is required and must be greater than or equal to 0.', type: 'error' });
      return;
    }
    if (dividendYield === '' || dividendYield < 0) {
      setToast({ message: 'Dividend Yield is required and must be greater than or equal to 0.', type: 'error' });
      return;
    }
    if (!tradingDate || !isValidDate(tradingDate)) {
      setToast({ message: 'Valid Trading Date is required.', type: 'error' });
      return;
    }

    const message = await onCheckPackageExists(normalizedPackageTitle);
    if (message) {
      setConfirmation(message);
    } else {
      onCreatePackage({
        symbol: normalizedSymbol,
        packageTitle: normalizedPackageTitle,
        description: trimmedDescription,
        entityPrice,
        interestRate: interestRate / 100,
        dividendYield: dividendYield / 100,
        tradingDate,
      }, false);
      resetState();
    }
  };

  const handleOverwrite = () => {
    const normalizedSymbol = symbol.trim().toUpperCase();
    const normalizedPackageTitle = packageTitle.trim().toUpperCase();
    const trimmedDescription = description.trim();
    onCreatePackage({
      symbol: normalizedSymbol,
      packageTitle: normalizedPackageTitle,
      description: trimmedDescription,
      entityPrice,
      interestRate: interestRate / 100,
      dividendYield: dividendYield / 100,
      tradingDate,
    }, true);
    resetState();
  };

  const resetState = () => {
    setSymbol('');
    setPackageTitle('');
    setDescription('');
    setEntityPrice('');
    setInterestRate('');
    setDividendYield('0');
    setTradingDate(new Date().toISOString().split('T')[0]);
    setConfirmation(null);
    onClose();
  }

  return (
    <div className="dialog-overlay">
      {confirmation ? (
        <div className="dialog new-package-dialog">
          <h2>Confirm Overwrite</h2>
          <p>{confirmation}</p>
          <div className="dialog-buttons">
            <button onClick={handleOverwrite} className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">
              Overwrite
            </button>
            <button onClick={resetState} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="dialog new-package-dialog">
          <h2>Create Option Package</h2>
          <label htmlFor="symbol">Entity Symbol</label>
          <input
            id="symbol"
            type="text"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            placeholder="Entity Symbol"
            maxLength={20}
          />
          <label htmlFor="packageTitle">Package Title</label>
          <input
            id="packageTitle"
            type="text"
            value={packageTitle}
            onChange={(e) => setPackageTitle(e.target.value.toUpperCase())}
            placeholder="Package Title"
            maxLength={50}
          />
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description"
            maxLength={500}
            rows={4}
          />
          <label htmlFor="entityPrice">Entity Price</label>
          <input
            id="entityPrice"
            type="number"
            step="0.01"
            min="0"
            value={entityPrice}
            onChange={(e) => setEntityPrice(Math.max(0, parseFloat(e.target.value)))}
            placeholder="Entity Price"
          />
          <label htmlFor="interestRate">Interest Rate (%)</label>
          <input
            id="interestRate"
            type="number"
            step="0.01"
            min="0"
            value={interestRate}
            onChange={(e) => setInterestRate(Math.max(0, parseFloat(e.target.value)))}
            placeholder="Interest Rate"
          />
          <label htmlFor="dividendYield">Dividend Yield (%)</label>
          <input
            id="dividendYield"
            type="number"
            step="0.01"
            min="0"
            value={dividendYield}
            onChange={(e) => setDividendYield(Math.max(0, parseFloat(e.target.value)))}
            placeholder="Dividend Yield"
          />
          <label htmlFor="tradingDate">Trading Date</label>
          <input
            id="tradingDate"
            type="date"
            value={tradingDate}
            onChange={(e) => setTradingDate(e.target.value)}
          />
          <div className="dialog-buttons">
            <button onClick={handleCreate} className="create-btn">
              Create
            </button>
            <button onClick={resetState} className="cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewPackageDialog;
